// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import {
  Currency,
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { useGooglePlacesPredictions } from '@/hooks/use-google-places-predictions';
import { PaymentTerm, paymentTermSchema } from '@/payment-term/api/types';
import { PaymentTermSearch } from '@/payment-term/components/payment-term-search';
import { UpdateProjectParameters } from '@/project/api/update-project';
import { ProjectBillingCard } from '@/project/components/project-billing-card';
import { useProjectContext } from '@/project/hooks/use-project-context';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import {
  PROJECT_INVOICED_CHARGE_LOCKED_TOOLTIP,
  deriveProjectCurrency,
  lineItemCurrencies,
  projectCurrencyLockedTooltip,
} from '@/project/utilities/project-currency';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  ClickAwayListener,
  Collapse,
  Divider,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { parseOneAddress } from 'email-addresses';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { z } from 'zod';

function isEmailAddress(input: string) {
  const parsed = parseOneAddress({ input, rejectTLD: true });

  return parsed !== null;
}

const emailSchema = z
  .string()
  .refine((input) => input === '' || isEmailAddress(input), {
    message: 'Invalid email address',
  });

const formSchema = z.object({
  billingAddress: z.string(),
  billingContactName: z.string(),
  billingContactEmail: emailSchema,
  billingContactPhone: z.string(),
  purchaseOrderNumber: z.string(),
  paymentTerm: paymentTermSchema.nullable(),
  discount: clearableCurrencyInputSchema,
  memo: z.string(),
});

interface FormInput {
  billingAddress: string;
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;
  purchaseOrderNumber: string;
  paymentTerm: PaymentTerm | null;
  discount: SerializableCurrency;
  memo: string;
}

type FormOutput = z.output<typeof formSchema>;

export const Route = createFileRoute(
  '/_app/projects/$projectKey/billing-details'
)({
  component: ProjectBillingDetailsRoute,
});

function ProjectBillingDetailsRoute() {
  const { projectKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="project"
      listPath="/projects"
      listLabel="Go to projects"
    >
      <ProjectBillingDetailsContent projectKey={projectKey} />
    </NotFoundErrorBoundary>
  );
}

function ProjectBillingDetailsContent(props: { projectKey: string }) {
  const { project } = useProjectContext();
  const { user } = useCurrentUser();

  const {
    search,
    predictions,
    reset: resetPredictions,
  } = useGooglePlacesPredictions();

  // The discount adopts the currency already locked for the project: an invoiced
  // charge freezes it regardless of which field set it; otherwise it locks to
  // the currency another field (the contract value or a line item) set, staying
  // editable only when the discount is the sole currency source.
  const projectCurrency =
    project.currency?.source === 'INVOICED_CHARGE'
      ? project.currency.isoCode
      : deriveProjectCurrency([
          project.totalContractValue,
          ...lineItemCurrencies(project.items),
        ]);
  const projectCurrencyTooltip = projectCurrency
    ? project.currency?.source === 'INVOICED_CHARGE'
      ? PROJECT_INVOICED_CHARGE_LOCKED_TOOLTIP
      : projectCurrencyLockedTooltip(projectCurrency)
    : undefined;

  const toDiscountFormValue = (value: Currency | null): SerializableCurrency =>
    toCurrencyInputValue(
      value,
      projectCurrency ?? user.organization.defaultCurrency
    );

  const {
    control,
    formState,
    setValue,
    setError,
    reset,
    register,
    handleSubmit,
  } = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      billingAddress: project.billingAddress ?? '',
      billingContactName: project.billingContactName ?? '',
      billingContactEmail: project.billingContactEmail ?? '',
      billingContactPhone: project.billingContactPhone ?? '',
      purchaseOrderNumber: project.purchaseOrderNumber ?? '',
      paymentTerm: project.paymentTerm,
      discount: toDiscountFormValue(project.discount),
      memo: project.memo ?? '',
    },
  });

  const updateProject = useUpdateProject(props.projectKey, {
    onSuccess: (project) => {
      reset({
        billingAddress: project.billingAddress ?? '',
        billingContactName: project.billingContactName ?? '',
        billingContactEmail: project.billingContactEmail ?? '',
        billingContactPhone: project.billingContactPhone ?? '',
        purchaseOrderNumber: project.purchaseOrderNumber ?? '',
        paymentTerm: project.paymentTerm,
        discount: toDiscountFormValue(project.discount),
        memo: project.memo ?? '',
      });
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (apiError.errorType === 'project_mixed_currency') {
        setError('discount.isoCode', {
          type: 'server',
          message: getUserFacingErrorMessage(apiError),
        });
      }
    },
  });

  const blocker = useBlocker({ condition: formState.isDirty });
  const paymentTermController = useController({ control, name: 'paymentTerm' });

  const options = predictions.map((prediction) => prediction.description);

  const handleChangePaymentTerm = (value: PaymentTerm | null) => {
    paymentTermController.field.onChange(value);
  };

  const handleClickCancel = () => {
    reset();
  };

  const handleValidSubmission: SubmitHandler<FormOutput> = (data) => {
    if (!formState.isDirty) {
      return;
    }

    const parameters: UpdateProjectParameters = {};

    if (formState.dirtyFields.billingAddress) {
      parameters.billingAddress = data.billingAddress;
    }

    if (formState.dirtyFields.billingContactName) {
      parameters.billingContactName = data.billingContactName;
    }

    if (formState.dirtyFields.billingContactEmail) {
      parameters.billingContactEmail = data.billingContactEmail;
    }

    if (formState.dirtyFields.billingContactPhone) {
      parameters.billingContactPhone = data.billingContactPhone;
    }

    if (formState.dirtyFields.purchaseOrderNumber) {
      parameters.purchaseOrderNumber = data.purchaseOrderNumber;
    }

    if (formState.dirtyFields.paymentTerm) {
      parameters.paymentTerm = data.paymentTerm?.id ?? null;
    }

    if (formState.dirtyFields.discount) {
      // A currency-dropdown change on an empty amount marks the field dirty
      // without changing the resolved value; only send a real change.
      const next = data.discount;
      const current = project.discount;
      const discountChanged =
        next === null
          ? current !== null
          : current === null ||
            !next.amount.equals(current.amount) ||
            next.isoCode !== current.isoCode;
      if (discountChanged) {
        parameters.discount = next;
      }
    }

    if (formState.dirtyFields.memo) {
      parameters.memo = data.memo;
    }

    updateProject.mutate(parameters);
  };

  return (
    <HardfinTabCardWrapper title="Billing">
      <Stack spacing={2} paddingTop={2}>
        <ProjectBillingCard projects={[project]} />
        <Paper
          variant="outlined"
          component="form"
          onSubmit={handleSubmit(handleValidSubmission)}
        >
          <UnsavedChangesDialog
            isOpen={blocker.status === 'blocked'}
            onCancel={blocker.reset}
            onProceed={blocker.proceed}
          />
          <Typography variant="subtitle2" padding={2}>
            Billing details for this project
          </Typography>
          <Divider />
          <Stack direction="row" spacing={2} padding={2}>
            <Stack spacing={2} flex={1}>
              <Typography variant="subtitle2">Contact info</Typography>
              <TextField
                size="small"
                label="Billing contact name"
                InputProps={register('billingContactName')}
              />
              <TextField
                size="small"
                label="Billing contact email"
                error={Boolean(formState.errors.billingContactEmail)}
                helperText={formState.errors.billingContactEmail?.message}
                InputProps={register('billingContactEmail')}
              />
              <TextField
                size="small"
                label="Billing contact phone"
                InputProps={register('billingContactPhone')}
              />
              <ClickAwayListener onClickAway={resetPredictions}>
                <div>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    label="Billing address"
                    InputProps={register('billingAddress', {
                      onChange: (event) => search(event.target.value),
                    })}
                  />
                  {options.length > 0 && (
                    <Paper
                      variant="outlined"
                      style={{ maxHeight: '10rem', overflow: 'auto' }}
                    >
                      <MenuList dense>
                        {options.map((option) => {
                          const handleClick = () => {
                            setValue('billingAddress', option);
                            resetPredictions();
                          };

                          return (
                            <MenuItem key={option} onClick={handleClick}>
                              {option}
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Paper>
                  )}
                </div>
              </ClickAwayListener>
            </Stack>
            <Stack spacing={2} flex={1}>
              <Typography variant="subtitle2">Invoice info</Typography>
              <PaymentTermSearch
                label="Payment term"
                value={paymentTermController.field.value}
                onChange={handleChangePaymentTerm}
              />
              <TextField
                size="small"
                label="PO number"
                InputProps={register('purchaseOrderNumber')}
              />
              <FormCurrencyTextField
                name="discount"
                label="Discount"
                placeholder="0.00"
                control={control}
                lockTo={projectCurrency ?? undefined}
                disabledTooltip={projectCurrencyTooltip}
              />
              <TextField
                multiline
                rows={4}
                size="small"
                label="Memo"
                InputProps={register('memo')}
              />
            </Stack>
          </Stack>
          <Collapse in={formState.isDirty}>
            <Stack
              direction="row"
              justifyContent="end"
              spacing={1}
              paddingX={2}
              paddingBottom={2}
            >
              <Button onClick={handleClickCancel}>Cancel</Button>
              <Button type="submit" variant="contained" color="secondary">
                Save
              </Button>
            </Stack>
          </Collapse>
        </Paper>
      </Stack>
    </HardfinTabCardWrapper>
  );
}
